import type { ReactNode } from 'react';

import { cn } from '@rocco/utils/cn';

import { RoccoLink } from '@rocco/components/link';
import { DetailLogoBadge } from './DetailLogoBadge';

export interface DetailBadgeRowProps {
    /** The brand behind the item. Linked to their profile when `link` is set. */
    logo?: {
        src: string;
        alt: string;
        background?: string | null;
        link?: string;
        ariaLabel?: string;
    };
    /**
     * Drawn in the badge's place when there is no logo — the article's own
     * ArchiPro mark. Without either, the row renders nothing.
     */
    fallback?: ReactNode;
    /** Extra classes on the row — a page whose band below it is layered higher passes its z-index here. */
    className?: string;
}

/**
 * The brand badge straddling the lower edge of a banner or gallery on the
 * right (Figma 2:552 — a 164px circle whose right edge sits 101px in from the
 * page edge, centred on the boundary), shared by the three detail pages so
 * the badge sits on the same pixel on each.
 *
 * The negative margins are what let it sit on that edge without occupying
 * it: the top one lifts it half its height onto the picture, the bottom one
 * gives the other half back, so what follows starts where it otherwise would.
 * The row spans the full width and is pulled over the band above it, so it
 * takes no pointer events of its own — only the badge and the pills do.
 *
 * Desktop only: mobile states the brand in its own trust row, and a badge
 * over the mobile picture would cover it.
 */
export const DetailBadgeRow = ({
    logo,
    fallback,
    className,
}: DetailBadgeRowProps) => {
    if (!logo && !fallback) return null;

    const badge = logo ? (
        logo.link ? (
            <RoccoLink
                to={logo.link}
                aria-label={logo.ariaLabel ?? `${logo.alt} profile`}
                className="pointer-events-auto block"
            >
                <DetailLogoBadge
                    src={logo.src}
                    alt={logo.alt}
                    background={logo.background}
                />
            </RoccoLink>
        ) : (
            <DetailLogoBadge
                src={logo.src}
                alt={logo.alt}
                background={logo.background}
                className="pointer-events-auto"
            />
        )
    ) : (
        fallback
    );

    return (
        <div
            className={cn(
                'pointer-events-none relative z-10 flex w-full justify-end px-[3rem] max-md:hidden md:-mt-[5.125rem] md:mb-[-5.125rem]',
                className,
            )}
        >
            <div className="relative md:mr-[3.3125rem]">{badge}</div>
        </div>
    );
};
